import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";

export type LivePeriodPartResult = {
  /** Id do LivePeriodScheduleSlot persistido no camera-meulance — path param do retry/desistir. */
  slotId?: number;
  part?: number | null;
  start: string;
  end: string;
  scheduleId?: number;
  youtubeId?: string;
  status?: "CREATED" | "FAILED" | "SKIPPED" | "ABANDONED";
  error?: string;
  /** Rótulo já traduzido pelo backend ("dd/MM - Parte N"/"Part N") — reenviar tal e qual num retry. */
  appendDescription?: string;
};

export type LivePeriodDayResult = {
  date: string;
  durationMinutes: number;
  cutTime?: string | null;
  parts: LivePeriodPartResult[];
};

export type LivePeriodResult = {
  liveId?: number;
  billedAmount?: number;
  /** True enquanto o período ainda tem partes pendentes e o link de pagamento não foi gerado. */
  periodPending?: boolean;
  /** PAID | PENDING | AWAITING_SCHEDULES | DIRECT_FINANCE — só populado por `useLivePeriodStatus`. */
  paymentStatus?: string;
  days: LivePeriodDayResult[];
};

export type LivePeriodPayload = {
  deviceId: number;
  playlistId: number;
  name?: string;
  privacy?: string;
  enableAudio?: boolean;
  musicPath?: string | null;
  directFinancePayment?: boolean;
  startDate: string;
  endDate: string;
  dailyStartTime: string;
  dailyEndTime: string;
};

/** Preview: roda validação/conflito no backend sem persistir nada. */
export function useLivePeriodPreview() {
  const [preview, setPreview] = useState<LivePeriodResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestSeq = useRef(0);

  const fetchPreview = useCallback(async (payload: LivePeriodPayload) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<{ data: LivePeriodResult }>(
        "/app/lives/private/period/preview",
        payload,
      );
      if (requestSeq.current !== seq) return null;
      setPreview(response.data?.data ?? null);
      return response.data?.data ?? null;
    } catch (err: any) {
      if (requestSeq.current !== seq) return null;
      const message =
        err?.response?.data?.message ?? err?.message ?? "Erro ao gerar prévia";
      setError(String(message));
      setPreview(null);
      return null;
    } finally {
      if (requestSeq.current === seq) setLoading(false);
    }
  }, []);

  const clear = useCallback(() => {
    requestSeq.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  return { preview, loading, error, fetchPreview, clear };
}

/** Criação real: gera a Live + um LiveSchedule por dia (Parte 1/Parte 2 quando aplicável). */
export function useCreateLivePeriod() {
  const [result, setResult] = useState<LivePeriodResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(async (payload: LivePeriodPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<{ data: LivePeriodResult }>(
        "/app/lives/private/period",
        payload,
      );
      const data = response.data?.data ?? null;
      setResult(data);
      return data;
    } catch (err: any) {
      const message =
        err?.response?.data?.message ?? err?.message ?? "Erro ao criar transmissão";
      setError(String(message));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { result, loading, error, create };
}

/**
 * Reprocessa manualmente uma parte com falha/pulada. Idêntico ao endpoint de criação original em
 * idempotência (`ensureSchedule` no camera-meulance: mesmo início/fim reusa o schedule em vez de
 * duplicar), mas agora identificado pelo `slotId` persistido em vez de reenviar início/fim/descrição.
 */
export function useRetryLivePeriodPart() {
  const [retryingSlotId, setRetryingSlotId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const retry = useCallback(async (liveId: number, slotId: number) => {
    setRetryingSlotId(slotId);
    setError(null);
    try {
      const response = await api.post<{ data: LivePeriodPartResult }>(
        `/app/lives/private/period/${liveId}/parts/${slotId}/retry`,
      );
      return response.data?.data ?? null;
    } catch (err: any) {
      const message = err?.response?.data?.message ?? err?.message ?? "Erro ao reprocessar parte";
      setError(String(message));
      throw err;
    } finally {
      setRetryingSlotId((current) => (current === slotId ? null : current));
    }
  }, []);

  return { retry, retryingSlotId, error };
}

/**
 * Desiste de uma parte com falha/pulada em vez de tentar de novo indefinidamente: marca ABANDONED
 * (sem chamar o YouTube) e exclui aquele dia do valor cobrado quando o billing for confirmado.
 */
export function useAbandonLivePeriodPart() {
  const [abandoningSlotId, setAbandoningSlotId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const abandon = useCallback(async (liveId: number, slotId: number) => {
    setAbandoningSlotId(slotId);
    setError(null);
    try {
      const response = await api.post<{ data: LivePeriodPartResult }>(
        `/app/lives/private/period/${liveId}/parts/${slotId}/abandon`,
      );
      return response.data?.data ?? null;
    } catch (err: any) {
      const message = err?.response?.data?.message ?? err?.message ?? "Erro ao desistir da parte";
      setError(String(message));
      throw err;
    } finally {
      setAbandoningSlotId((current) => (current === slotId ? null : current));
    }
  }, []);

  return { abandon, abandoningSlotId, error };
}

/**
 * Faz polling do status do período enquanto `periodPending` for true (mesmo padrão de
 * `Payment.tsx`: intervalo de 4s), parando sozinho assim que o billing é confirmado.
 */
export function useLivePeriodStatus(liveId: number | null | undefined) {
  const [status, setStatus] = useState<LivePeriodResult | null>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!liveId) return undefined;
    let canceled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const tick = async () => {
      try {
        const response = await api.get<{ data: LivePeriodResult }>(
          `/app/lives/private/period/${liveId}/status`,
        );
        if (canceled) return;
        const data = response.data?.data ?? null;
        setStatus(data);
        if (data && data.periodPending === false && timer) {
          clearInterval(timer);
          timer = null;
          setPolling(false);
        }
      } catch {
        // silencioso — a próxima tentativa do intervalo cobre falhas transitórias
      }
    };

    setPolling(true);
    void tick();
    timer = setInterval(tick, 4000);

    return () => {
      canceled = true;
      if (timer) clearInterval(timer);
      setPolling(false);
    };
  }, [liveId]);

  return { status, polling };
}
